import { computed } from 'vue';
import type { OrgBusinessHours, TransitOrganization, TransitOrganizationId } from '@/types';
import { stlOrganization, stmOrganization } from '@/config';
import { useI18n } from 'vue-i18n';
import { dayjs, formatDateTime } from '@/utils/dateTime.ts';
import useLocale from '@/composables/useLocale.ts';
import { U_NARROW_NO_BREAK_SPACE } from '@/utils/unicodeCharacters.ts';
import type { Dayjs } from 'dayjs';

const transitConfig: Record<TransitOrganizationId, TransitOrganization> = {
  stl: stlOrganization,
  stm: stmOrganization,
};

export default function useTransitOrganization(orgId: TransitOrganizationId) {
  const { t } = useI18n();
  const locale = useLocale();

  const config = computed<TransitOrganization>(() => transitConfig[orgId]);

  const customerSupportPhoneLink = computed<string>(() => {
    const phone = config.value.paratransit.bookingService.phone;
    return phone.number + (phone.option ? `;${phone.option}` : '');
  });

  const customerServicePhoneLink = computed<string>(() => {
    const phone = config.value.paratransit.customerService.phone;
    return phone.number + (phone.option ? `;${phone.option}` : '');
  });

  const emailAddress = computed<string | null>(() => config.value.paratransit.customerService.email);

  const customerSupportHoursLabel = computed<string>(() => buildBusinessHoursLabel(config.value.paratransit.bookingService.openingHours));

  const websiteSupportHoursLabel = computed<string>(() => buildBusinessHoursLabel(config.value.paratransit.assistanceService.openingHours));

  const customerServiceHoursLabel = computed<string>(() => buildBusinessHoursLabel(config.value.paratransit.customerService.openingHours));

  function buildBusinessHoursLabel(data: OrgBusinessHours): string {
    let output = t(
      data.openingDays === 'all' ? 'generic.openingDays.allDays' : 'generic.openingDays.weekdays',
      {
        lowerBound: localizeTimeValue(dayjs(data.lowerBound, 'HH:mm')),
        upperBound: localizeTimeValue(dayjs(data.upperBound, 'HH:mm')),
      },
    );

    if (typeof data.holidayOpeningHours === 'undefined') {
      return output;
    }

    output += data.holidayOpeningHours == false
      ? `\n(${t('generic.openingDays.closedOnHolidays')})`
      : `\n(${t('generic.openingDays.openedOnHolidays', {
        lowerBound: localizeTimeValue(dayjs(data.holidayOpeningHours.lowerBound, 'HH:mm')),
        upperBound: localizeTimeValue(dayjs(data.holidayOpeningHours.upperBound, 'HH:mm')),
      })})`;

    return output;
  }

  function localizeTimeValue(timeValue: Dayjs) {

    if (locale.value === 'fr') {
      return formatDateTime(timeValue, { destinationFormat: 'H:mm' }).replace(':', 'h');
    }

    return formatDateTime(timeValue, { destinationFormat: 'LT' })
      .replace(' PM', `${U_NARROW_NO_BREAK_SPACE}p.m.`)
      .replace(' AM', `${U_NARROW_NO_BREAK_SPACE}a.m.`);

  }

  return {
    customerSupportPhoneLink,
    customerServicePhoneLink,
    customerSupportHoursLabel,
    websiteSupportHoursLabel,
    customerServiceHoursLabel,
    emailAddress,
  };
}
